import { RefreshCw01Icon } from '@langchain/untitled-ui-icons';
import { Button, CircularProgress, Switch } from '@mui/joy';

import { TextOverflowTooltip } from '@/components/TextOverflowTooltip';
import { Tooltip } from '@/design-system';
import { useGetTrackedComparisonRun } from '@/hooks/useGetComparisonRun';
import { useTraceCompare } from '@/hooks/useTraceCompare';
import { RunSchema } from '@/types/schema';
import { cn } from '@/utils/tailwind';

export const RunDetailsPanelHeader = ({
  isLoading,
  compareRunId,
  peekedRunId,
  peekedRunData,
  comparisonRunData,
  handleUpdateAutoTrackingIndex,
  compareProps,
  datasetShareToken,
  onRefreshRun,
}: {
  isLoading: boolean;
  compareRunId: string | null;
  peekedRunId: string | null;
  peekedRunData?: RunSchema;
  comparisonRunData?: RunSchema;
  handleUpdateAutoTrackingIndex: ReturnType<
    typeof useGetTrackedComparisonRun
  >['handleUpdateAutoTrackingIndex'];
  compareProps: ReturnType<typeof useTraceCompare>;
  datasetShareToken?: string;
  onRefreshRun: () => void;
}) => {
  const {
    isAutoTracking,
    setIsAutoTracking,
    isScrollingSynced,
    setIsScrollingSynced,
    isShowingDiff,
    setIsShowingDiff,

    handleSelectCompare,
    resetCompareState,
    setSelectingCompare,
  } = compareProps;

  return (
    <>
      <div className="flex flex-1 items-center">
        {isLoading ? (
          <>
            <div className="mx-2 h-[32px] self-stretch border-r border-r-secondary" />
            <CircularProgress
              size="sm"
              color="neutral"
              sx={{ opacity: 0.5, transform: 'scale(0.83)' }}
            />
          </>
        ) : (
          <>
            <div className="mx-2 h-[32px] self-stretch border-r border-r-secondary" />
            <Tooltip title="Refresh run">
              <div>
                <RefreshCw01Icon
                  className={cn(
                    'h-6 w-6 cursor-pointer rounded p-1 opacity-50 transition-colors hover:bg-black/10 dark:hover:bg-white/10'
                  )}
                  onClick={onRefreshRun}
                />
              </div>
            </Tooltip>

            {compareRunId && (
              <>
                <div className="mx-2 h-[32px] self-stretch border-r border-r-secondary" />

                <TextOverflowTooltip className="flex-1">
                  Comparing {peekedRunData?.name} to {comparisonRunData?.name}{' '}
                </TextOverflowTooltip>
              </>
            )}
          </>
        )}
      </div>

      <div className="flex flex-none select-none items-center justify-end gap-4">
        {compareRunId && (
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Switch
                size="sm"
                checked={isScrollingSynced && isAutoTracking}
                onChange={(e) => {
                  const { checked } = e.target;
                  setIsScrollingSynced(checked);
                  setIsAutoTracking(checked);
                  if (checked && peekedRunId) {
                    handleUpdateAutoTrackingIndex(peekedRunId, 'peeked');
                  }
                }}
              />
              <div className="text-[14px] text-primary">Sync</div>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                size="sm"
                checked={isShowingDiff}
                onChange={(e) => setIsShowingDiff(e.target.checked)}
                data-testid="diff-toggle"
              />
              <div className="text-[14px] text-primary">Diff</div>
            </div>
          </div>
        )}
        {peekedRunId && !datasetShareToken && (
          <Button
            size="sm"
            color="neutral"
            variant="outlined"
            onClick={() => {
              if (compareRunId) {
                handleSelectCompare(null);
              } else {
                setSelectingCompare(true);
              }
              resetCompareState();
            }}
          >
            {compareRunId ? 'Stop comparing' : 'Compare'}
          </Button>
        )}
      </div>
    </>
  );
};
